import { useEffect, useState } from "react";
import { Layout } from "./components/layout/Layout";
import { supabaseReservation } from "../../../integrations/supabase/client";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Label } from "../../ui/label";
import { useToast } from "../../../hooks/use-toast";
import { Save, Loader2, Plus, Trash2, Calendar, Link, FileText, Clock, Building2, Wallet, Mic, MessageSquare, MessagesSquare } from "lucide-react";
import { OutletManager } from "./components/settings/OutletManager";
import { CreditsManager } from "./components/settings/CreditsManager";
import { Calendar as CalendarComponent } from "../../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { format } from "date-fns";
import { cn } from "../../../lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Switch } from "../../ui/switch";
import { useAuth } from "../../../contexts/AuthContext";

interface AgentConfig {
  id: string;
  restaurant_name: string;
  restaurant_hours: string;
  menu: string;
  menu_type: string;
  menu_url: string | null;
  instructions: string;
  reservations_enabled: boolean;
  communication_mode: 'voice' | 'chat' | 'both';
}

interface BlockedDate {
  id: string;
  block_date: string;
  block_time: string | null;
  reason: string | null;
  is_full_day: boolean;
  outlet_id: string | null;
}

interface Outlet {
  id: string;
  name: string;
}

interface SettingProps {
  props: any;
  outletList:string[];
  onRecallOulet: (recall: boolean) => void;
  showAddOutlet:boolean;
}


const Settings = ({ props, outletList, onRecallOulet, showAddOutlet }: SettingProps) => {
  const { toast } = useToast();
  const { accountId } = useAuth();
  const [config, setConfig] = useState<AgentConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [blockedDates, setBlockedDates] = useState<BlockedDate[]>([]);
  const [outlets, setOutlets] = useState<Outlet[]>([]);
  const [newBlockDate, setNewBlockDate] = useState<Date | undefined>();
  const [newBlockTime, setNewBlockTime] = useState("");
  const [newBlockReason, setNewBlockReason] = useState("");
  const [newBlockOutletId, setNewBlockOutletId] = useState<string>("all");
  const [isFullDay, setIsFullDay] = useState(true);
  const [addingBlock, setAddingBlock] = useState(false);

  useEffect(() => {
    fetchConfig();
    fetchBlockedDates();
    fetchOutlets();
  }, []);

  const fetchConfig = async () => {
    const { data, error } = await supabaseReservation
      .from('agent_config')
      .select('*')
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('Error fetching config:', error);
      toast({
        title: "Error",
        description: "Failed to load settings",
        variant: "destructive",
      });
    } else if (data) {
      setConfig({
        ...data,
        communication_mode: (data.communication_mode as 'voice' | 'chat' | 'both') || 'voice'
      });
    }
    setLoading(false);
  };

  const fetchBlockedDates = async () => {
    const { data, error } = await supabaseReservation
      .from('blocked_dates')
      .select('*')
      .order('block_date', { ascending: true });

    if (error) {
      console.error('Error fetching blocked dates:', error);
    } else {
      setBlockedDates(data || []);
    }
  };

  const fetchOutlets = async () => {
    const { data, error } = await supabaseReservation
      .from('outlets')
      .select('id, name')
      .eq('is_active', true)
      .order('name');

    if (error) {
      console.error('Error fetching outlets:', error);
    } else {
      setOutlets(data || []);
    }
  };

  const handleSave = async () => {
    if (!config) return;

    setSaving(true);
    
    // If menu type is link, pre-fetch and cache the content
    let menuContent = config.menu;
    if (config.menu_type === 'link' && config.menu_url) {
      toast({
        title: "Fetching Menu",
        description: "Scraping menu content from the URL...",
      });
      
      try {
        const { data, error: fetchError } = await supabaseReservation.functions.invoke('fetch-menu', {
          body: { url: config.menu_url, forceRefresh: true }
        });
        
        if (fetchError || !data?.success) {
          toast({
            title: "Warning",
            description: "Could not fetch menu content. Settings will be saved without cached menu.",
            variant: "destructive",
          });
        } else {
          menuContent = data.content;
          toast({
            title: "Menu Cached",
            description: `Menu content fetched (${data.content.length} characters)`,
          });
        }
      } catch (err) {
        console.error('Error fetching menu:', err);
      }
    }
    
    const { error } = await supabaseReservation
      .from('agent_config')
      .update({
        restaurant_name: config.restaurant_name,
        restaurant_hours: config.restaurant_hours,
        menu: menuContent,
        menu_type: config.menu_type,
        menu_url: config.menu_url,
        instructions: config.instructions,
        reservations_enabled: config.reservations_enabled,
        communication_mode: config.communication_mode,
      })
      .eq('id', config.id);

    if (error) {
      console.error('Error saving config:', error);
      toast({
        title: "Error",
        description: "Failed to save settings",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Settings Saved",
        description: "Your agent configuration has been updated",
      });
    }
    setSaving(false);
  };

  const handleChange = (field: keyof AgentConfig, value: string) => {
    if (config) {
      setConfig({ ...config, [field]: value });
    }
  };

  const addBlockedDate = async () => {
    if (!newBlockDate) return;

    setAddingBlock(true);
    const { error } = await supabaseReservation
      .from('blocked_dates')
      .insert({
        block_date: format(newBlockDate, 'yyyy-MM-dd'),
        block_time: isFullDay ? null : newBlockTime || null,
        reason: newBlockReason || null,
        is_full_day: isFullDay,
        outlet_id: newBlockOutletId === "all" ? null : newBlockOutletId,
        account_id: accountId,
      });

    if (error) {
      console.error('Error adding blocked date:', error);
      toast({
        title: "Error",
        description: "Failed to add blocked date",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Date Blocked",
        description: `${format(newBlockDate, 'PPP')} has been blocked`,
      });
      setNewBlockDate(undefined);
      setNewBlockTime("");
      setNewBlockReason("");
      setNewBlockOutletId("all");
      setIsFullDay(true);
      fetchBlockedDates();
    }
    setAddingBlock(false);
  };

  const removeBlockedDate = async (id: string) => {
    const { error } = await supabaseReservation
      .from('blocked_dates')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error removing blocked date:', error);
      toast({
        title: "Error",
        description: "Failed to remove blocked date",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Block Removed",
        description: "The date block has been removed",
      });
      fetchBlockedDates();
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Settings</h1>
          <p className="text-muted-foreground mt-1">Configure your AI voice agent</p>
        </div>

        {/* Agent Configuration */}
        <div className="rounded-xl border border-border bg-card p-6 space-y-6">
          <h2 className="text-lg font-semibold text-card-foreground">Agent Configuration</h2>
          
          {/* Reservations Toggle */}
          <div className="flex items-center justify-between p-4 rounded-lg bg-background border border-border">
            <div className="space-y-0.5">
              <Label htmlFor="reservations_enabled" className="text-base font-medium text-card-foreground">
                Enable Reservations
              </Label>
              <p className="text-sm text-muted-foreground">
                Allow the AI agent to take reservations. When disabled, the agent will only answer questions about your business.
              </p>
            </div>
            <Switch
              id="reservations_enabled"
              checked={config?.reservations_enabled ?? true}
              onCheckedChange={(checked) => {
                if (config) {
                  setConfig({ ...config, reservations_enabled: checked });
                }
              }}
            />
          </div>

          {/* Communication Mode Selector */}
          <div className="space-y-3 p-4 rounded-lg bg-background border border-border">
            <div className="space-y-0.5">
              <Label className="text-base font-medium text-card-foreground">
                Communication Mode
              </Label>
              <p className="text-sm text-muted-foreground">
                Choose how customers can interact with your AI agent in the embed widget
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => config && setConfig({ ...config, communication_mode: 'voice' })}
                className={cn(
                  "flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all",
                  config?.communication_mode === 'voice'
                    ? "border-primary bg-primary/10"
                    : "border-border hover:border-muted-foreground"
                )}
              >
                <Mic className={cn(
                  "h-6 w-6",
                  config?.communication_mode === 'voice' ? "text-primary" : "text-muted-foreground"
                )} />
                <span className={cn(
                  "text-sm font-medium",
                  config?.communication_mode === 'voice' ? "text-primary" : "text-muted-foreground"
                )}>Voice</span>
              </button>
              <button
                type="button"
                onClick={() => config && setConfig({ ...config, communication_mode: 'chat' })}
                className={cn(
                  "flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all",
                  config?.communication_mode === 'chat'
                    ? "border-primary bg-primary/10"
                    : "border-border hover:border-muted-foreground"
                )}
              >
                <MessageSquare className={cn(
                  "h-6 w-6",
                  config?.communication_mode === 'chat' ? "text-primary" : "text-muted-foreground"
                )} />
                <span className={cn(
                  "text-sm font-medium",
                  config?.communication_mode === 'chat' ? "text-primary" : "text-muted-foreground"
                )}>Chat</span>
              </button>
              <button
                type="button"
                onClick={() => config && setConfig({ ...config, communication_mode: 'both' })}
                className={cn(
                  "flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all",
                  config?.communication_mode === 'both'
                    ? "border-primary bg-primary/10"
                    : "border-border hover:border-muted-foreground"
                )}
              >
                <MessagesSquare className={cn(
                  "h-6 w-6",
                  config?.communication_mode === 'both' ? "text-primary" : "text-muted-foreground"
                )} />
                <span className={cn(
                  "text-sm font-medium",
                  config?.communication_mode === 'both' ? "text-primary" : "text-muted-foreground"
                )}>Both</span>
              </button>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="restaurant_name" className="text-card-foreground">
              Restaurant Name
            </Label>
            <Input
              id="restaurant_name"
              value={config?.restaurant_name || ''}
              onChange={(e) => handleChange('restaurant_name', e.target.value)}
              placeholder="Enter your restaurant name"
              className="bg-background border-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="restaurant_hours" className="text-card-foreground">
              Restaurant Hours
            </Label>
            <Textarea
              id="restaurant_hours"
              value={config?.restaurant_hours || ''}
              onChange={(e) => handleChange('restaurant_hours', e.target.value)}
              placeholder="Enter your operating hours"
              rows={3}
              className="bg-background border-input resize-none"
            />
          </div>

          {/* Menu Type Selection */}
          <div className="space-y-4">
            <Label className="text-card-foreground">Menu</Label>
            <Select
              value={config?.menu_type || 'text'}
              onValueChange={(value) => handleChange('menu_type', value)}
            >
              <SelectTrigger className="w-full bg-background border-input">
                <SelectValue placeholder="Select menu type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="text">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Text Description
                  </div>
                </SelectItem>
                <SelectItem value="pdf">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    PDF Link
                  </div>
                </SelectItem>
                <SelectItem value="link">
                  <div className="flex items-center gap-2">
                    <Link className="h-4 w-4" />
                    Website Link
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>

            {config?.menu_type === 'text' && (
              <Textarea
                id="menu"
                value={config?.menu || ''}
                onChange={(e) => handleChange('menu', e.target.value)}
                placeholder="Describe your menu items"
                rows={5}
                className="bg-background border-input resize-none"
              />
            )}

            {(config?.menu_type === 'pdf' || config?.menu_type === 'link') && (
              <Input
                value={config?.menu_url || ''}
                onChange={(e) => handleChange('menu_url', e.target.value)}
                placeholder={config?.menu_type === 'pdf' ? 'https://example.com/menu.pdf' : 'https://example.com/menu'}
                className="bg-background border-input"
              />
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="instructions" className="text-card-foreground">
              Custom Instructions
            </Label>
            <Textarea
              id="instructions"
              value={config?.instructions || ''}
              onChange={(e) => handleChange('instructions', e.target.value)}
              placeholder="Add custom behavior instructions for the AI agent"
              rows={4}
              className="bg-background border-input resize-none"
            />
            <p className="text-xs text-muted-foreground">
              These instructions guide the AI's behavior and responses
            </p>
          </div>

          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Settings
              </>
            )}
          </Button>
        </div>

        {/* Outlets & Time Slots */}
        <div className="rounded-xl border border-border bg-card p-6 space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-card-foreground flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Outlets & Time Slots
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Manage your outlets and their booking time slots
            </p>
          </div>
          <OutletManager outletList={outletList} onRecallOulet={(recall) => { onRecallOulet(recall); }} showAddOutlet={showAddOutlet} />
        </div>

        {/* Blocked Dates */}
        <div className="rounded-xl border border-border bg-card p-6 space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-card-foreground">Blocked Dates</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Block specific dates or times from accepting reservations
            </p>
          </div>

          {/* Add New Block */}
          <div className="space-y-4 p-4 rounded-lg bg-background border border-border">
            <div className="flex flex-col sm:flex-row gap-4">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full sm:w-[200px] justify-start text-left font-normal",
                      !newBlockDate && "text-muted-foreground"
                    )}
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    {newBlockDate ? format(newBlockDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={newBlockDate}
                    onSelect={setNewBlockDate}
                    initialFocus
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>

              <div className="flex items-center gap-2">
                <Switch
                  checked={isFullDay}
                  onCheckedChange={setIsFullDay}
                />
                <Label className="text-sm">Full day</Label>
              </div>

              {!isFullDay && (
                <Input
                  type="time"
                  value={newBlockTime}
                  onChange={(e) => setNewBlockTime(e.target.value)}
                  className="w-full sm:w-[140px] bg-background"
                  placeholder="Time"
                />
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Select
                value={newBlockOutletId}
                onValueChange={setNewBlockOutletId}
              >
                <SelectTrigger className="w-full sm:w-[200px] bg-background">
                  <SelectValue placeholder="Select outlet" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Outlets</SelectItem>
                  {outlets.map((outlet) => (
                    <SelectItem key={outlet.id} value={outlet.id}>
                      {outlet.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Input
                value={newBlockReason}
                onChange={(e) => setNewBlockReason(e.target.value)}
                placeholder="Reason (optional) - e.g., Private event, Holiday"
                className="bg-background flex-1"
              />
            </div>

            <Button
              onClick={addBlockedDate}
              disabled={!newBlockDate || addingBlock}
              className="w-full"
              variant="secondary"
            >
              {addingBlock ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              Add Blocked Date
            </Button>
          </div>

          {/* Blocked Dates List */}
          <div className="space-y-2">
            {blockedDates.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No dates blocked
              </p>
            ) : (
              blockedDates.map((block) => (
                <div
                  key={block.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-background border border-border"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-foreground">
                        {format(new Date(block.block_date), "PPP")}
                        {!block.is_full_day && block.block_time && (
                          <span className="text-muted-foreground ml-2">
                            at {block.block_time}
                          </span>
                        )}
                      </p>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                        {block.outlet_id 
                          ? outlets.find(o => o.id === block.outlet_id)?.name || "Specific outlet"
                          : "All outlets"}
                      </span>
                    </div>
                    {block.reason && (
                      <p className="text-sm text-muted-foreground">{block.reason}</p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeBlockedDate(block.id)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Credits & Billing */}
        <div className="rounded-xl border border-border bg-card p-6 space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-card-foreground flex items-center gap-2">
              <Wallet className="h-5 w-5" />
              Credits & Billing
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Manage your voice agent usage credits
            </p>
          </div>
          <CreditsManager />
        </div>

        {/* Twilio Configuration Info */}
        <div className="rounded-xl border border-border bg-card p-6">
          <h3 className="font-semibold text-card-foreground mb-4">Twilio Phone Integration</h3>
          <p className="text-sm text-muted-foreground mb-4">
            To enable phone calls, configure your Twilio phone number with this webhook URL:
          </p>
          <code className="block p-3 rounded-lg bg-background text-sm text-primary break-all">
            {/* ${/* import.meta.env.VITE_SUPABASE_URL */}
            {`/functions/clientpanel/enage/twilio-voice/twiml`}
          </code>
          <p className="text-xs text-muted-foreground mt-3">
            Set this as a Webhook with HTTP POST method in your Twilio Console under Phone Numbers → Voice Configuration
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default Settings;
